import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $cardListStore, $profileFeatureStore } from '@lib/store';
import { useListState, useRouteHook } from '@shared/hooks';
import { cn, FormatUtils, PaletteUtils, RoutePaths } from '@shared/utils';
import { CardStatus } from '@shared/api';
import { CardItem } from '../cardItem';
import { View } from '@widgets';
import { Pagination, Select, Table, TableCellType, Text } from '@shared/widgets';

interface CardsProps {
  className?: string;
  title?: ReactNode;
  loading?: boolean;
  listType?: boolean;
  currency?: string;
  accountId?: number;
  accountIds?: number[];
}

export const Cards = (props: CardsProps) => {
  const { navigate } = useRouteHook();

  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const cardListStore = useUnit($cardListStore.store);

  const [status, setStatus] = useState<CardStatus>();
  const cardsState = useListState({ list: 'cards' });

  const { t } = useTranslation('accounts');

  useEffect(() => {
    return () => $cardListStore.events.clear();
  }, []);

  useEffect(() => {
    cardsState.onChangePage(1);
  }, [props.accountIds, props.currency]);

  useEffect(() => {
    if (props.loading) return;
    $cardListStore.events.getCards({
      AccountId: props.accountId,
      AccountIds: props.accountIds,
      Currency: props.currency,
      Status: status,
      Count: cardsState.count,
      FromIndex: cardsState.fromIndex,
      ReturnTotalCount: true,
    });
  }, [props.loading, props.accountId, props.accountIds, props.currency, status, cardsState]);

  const statusOptions = useMemo(
    () => Object.entries(CardStatus).map(([value, label]) => ({ label: t(`statuses.card.${label}`, { ns: 'common' }), value: value as CardStatus })),
    [t],
  );
  const filteredCards = useMemo(() => {
    if (!props.accountIds) return cardListStore.data?.data || [];
    return cardListStore.data?.data?.filter((card) => props.accountIds?.includes(card.accountId)) || [];
  }, [cardListStore.data?.data, props.accountIds]);
  const columns = useMemo(
    () => [
      t('card.relatedCardColumns.cardholder'),
      t('card.relatedCardColumns.card'),
      t('card.relatedCardColumns.type'),
      t('card.relatedCardColumns.product'),
      t('card.relatedCardColumns.status'),
      t('card.relatedCardColumns.account'),
      t('card.relatedCardColumns.currency'),
    ],
    [t],
  );
  return (
    <section
      className={cn(
        'flex flex-col gap-6',
        PaletteUtils.isVcfcPalette && 'rounded-xl bg-white p-6 dark:border dark:border-gray-black dark:bg-soft-black',
        props.className,
      )}
    >
      <div className='flex items-center justify-between'>
        {props.title && (
          <Text weight='semibold' size={PaletteUtils.isInsigniaPalette ? '2xl' : 'lg'}>
            {props.title}
          </Text>
        )}
        <Select
          className='ml-auto min-w-[150px]'
          nullValue={{ isEnable: true, text: t('placeholders.allStatuses', { ns: 'common' }) }}
          items={statusOptions}
          value={status}
          onChange={setStatus}
        />
      </div>
      <View
        loading={props.loading || cardListStore.loading}
        error={cardListStore.error}
        onRefresh={() =>
          $cardListStore.events.getCards({
            AccountId: props.accountId,
            AccountIds: props.accountIds,
            Currency: props.currency,
            Status: status,
            Count: cardsState.count,
            FromIndex: cardsState.fromIndex,
            ReturnTotalCount: true,
          })
        }
        noData={!filteredCards.length}
      >
        <div className='flex flex-col gap-6'>
          {props.listType && (
            <Table
              isScrollable
              columns={columns}
              items={filteredCards.map((card) => [
                {
                  type: TableCellType.Custom,
                  value: (
                    <div className='flex shrink-0 items-center'>
                      <div className='relative mr-3 flex h-8 w-8 flex-none select-none items-center justify-center overflow-hidden rounded-full text-sm'>
                        <div className='relative z-[1] flex h-full w-full items-center justify-center rounded bg-primary text-soft-black dark:text-white'>
                          <Text weight='medium' size='xl' color={PaletteUtils.getColor('black', 'white')}>
                            {card.cardHolder?.split('')[0]}
                          </Text>
                        </div>
                      </div>
                      <div className='flex-1 py-0.5'>
                        <Text className='overflow-hidden text-ellipsis' size='sm'>
                          {card.cardHolder}
                        </Text>
                      </div>
                    </div>
                  ),
                },
                { type: TableCellType.Text, value: FormatUtils.toSliceNumber(card.cardNumber) },
                { type: TableCellType.Text, value: t(`card.type.${card.cardType.toLowerCase()}`) },
                { type: TableCellType.Text, value: card.productDisplayName },
                { type: TableCellType.Text, value: t(`statuses.card.${card.status}`, { ns: 'common' }) },
                { type: TableCellType.Text, value: FormatUtils.toSliceNumber(card.accountNumber) },
                { type: TableCellType.Text, value: card.currency.toUpperCase() },
              ])}
              onClick={(rowIndex) => {
                const cardId = filteredCards.find((_, index) => rowIndex === index)?.cardId;
                const viewCardDetails = profileFeatureStore.data?.cardFlows?.find((feature) => feature.cardId === cardId)?.viewCardDetails;
                if (viewCardDetails === false) return;
                navigate(RoutePaths.card.onNavigate(cardId!));
              }}
            />
          )}
          {!props.listType && (
            <div
              className={cn(
                'rounded-tr-2 rounded-br-2 rounded-bl-2 flex w-full flex-wrap gap-x-[26px] gap-y-[30px] rounded-tl-none bg-white pb-[30px] dark:bg-soft-black',
                PaletteUtils.isInsigniaPalette && 'pt-5',
                PaletteUtils.isVcfcPalette && 'max-md:justify-center',
              )}
            >
              {filteredCards.map((card, idx) => {
                const viewCardDetails = profileFeatureStore.data?.cardFlows?.find((feature) => feature.cardId === card.cardId)?.viewCardDetails;
                return (
                  <Link key={idx} to={RoutePaths.card.onNavigate(card.cardId)} onClick={(e) => viewCardDetails === false && e.preventDefault()}>
                    <CardItem card={card} withHover />
                  </Link>
                );
              })}
            </div>
          )}
          <Pagination
            perPageLabel={props.listType ? undefined : t('pagination.perPage.items', { ns: 'common' })}
            totalItemCount={cardListStore.data?.total || 0}
            currentPage={cardsState.page}
            countItemPage={cardsState.count}
            onChangePage={cardsState.onChangePage}
            onChangeCountItemPage={cardsState.onChangeCount}
          />
        </div>
      </View>
    </section>
  );
};
